import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card } from 'react-bootstrap';
import ChannelColumn from './ChannelColumn';

// ChannelList renders a horizontal list of channels using ChannelColumn
export default function ChannelList({ category, channels, onReorder, getEntryValue, setEntryValue, exportChannel, importChannel, groups = [] }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [menuRect, setMenuRect] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const nameRefs = useRef([]);
  const nameDownRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleDragStart() {
    setIsDragging(true);
  }

  function handleDragEnd(result) {
    setIsDragging(false);
    if (!result.destination) return;
    const src = result.source.index;
    const dest = result.destination.index;
    if (src === dest) return;
    const next = Array.from(channels);
    const [moved] = next.splice(src, 1);
    next.splice(dest, 0, moved);
    onReorder(category, next);
  }

  return (
    <Card className={`mb-3 h-100 ${category === 'a' ? 'auxes' : ''}`}>
      <Card.Body className="d-flex flex-column h-100">
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <Droppable droppableId={category} direction="horizontal">
            {(provided) => (
              <div className="channels-area">
                <div className="channels-scroll" ref={provided.innerRef} {...provided.droppableProps}>
                  <div className="channels-row-inner">
                    {channels.map((ch, idx) => (
                      <Draggable key={ch.index} draggableId={`${category}-${ch.index}`} index={idx}>
                        {(prov) => (
                          <ChannelColumn
                            prov={prov}
                            category={category}
                            ch={ch}
                            idx={idx}
                            getEntryValue={getEntryValue}
                            setEntryValue={setEntryValue}
                            groups={groups}
                            nameRef={(el) => (nameRefs.current[idx] = el)}
                            onNamePointerDown={(e) => { nameDownRef.current = { x: e.clientX, y: e.clientY }; }}
                            onNameClick={(e) => {
                              const down = nameDownRef.current;
                              nameDownRef.current = null;
                              if (down) {
                                const dx = Math.abs(e.clientX - down.x);
                                const dy = Math.abs(e.clientY - down.y);
                                if (dx + dy > 6) return; // was a drag
                              }
                              if (isDragging) return;
                              const same = openMenu === idx;
                              if (same) {
                                setOpenMenu(null);
                                setMenuRect(null);
                                return;
                              }
                              setOpenMenu(idx);
                              const r = (e.target && e.target.getBoundingClientRect && e.target.getBoundingClientRect());
                              setMenuRect(r);
                              if (r) {
                                const menuW = 220;
                                const centerX = r.left + r.width / 2;
                                const left = Math.min(Math.max(centerX - menuW / 2, 8), window.innerWidth - menuW - 8);
                                setMenuPos({ left, top: r.top });
                              }
                            }}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
                <div className="master-wrapper">
                  <ChannelColumn prov={null} category={category} ch={{ index: 'master', name: 'MASTER' }} idx={-1} getEntryValue={getEntryValue} setEntryValue={setEntryValue} groups={groups} isMaster={true} />
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Render menu portal for the open channel */}
        {openMenu != null && menuPos && (
          ReactDOM.createPortal(
            <div className="channel-menu" style={{ left: menuPos.left, top: menuPos.top, transform: 'translateY(-100%)' }}>
                {category !== 'a' && (
                  <div className="group-list">
                    <button
                      className={((getEntryValue ? parseInt(getEntryValue(category, channels[openMenu].index, 'subgroup') || -1) : -1) === -1) ? 'active' : ''}
                      onClick={() => { if (setEntryValue) setEntryValue(category, channels[openMenu].index, 'subgroup', -1); setOpenMenu(null); }}
                    >
                      NO GROUP
                    </button>

                    {groups.map((g, gi) => {
                      const cur = getEntryValue ? parseInt(getEntryValue(category, channels[openMenu].index, 'subgroup') || -1) : -1;
                      const selected = cur === g.index;
                      return (
                        <button key={`group-${g.index}`} className={selected ? 'active' : ''} onClick={() => { if (setEntryValue) setEntryValue(category, channels[openMenu].index, 'subgroup', g.index); setOpenMenu(null); }}>
                          {g.name || `GROUP ${gi + 1}`}
                        </button>
                      );
                    })}
                  </div>
                )}

              <div className="menu-actions">
                <div className="rename-row">
                  <button onClick={() => { const newName = window.prompt('Rename channel', channels[openMenu].name || ''); if (newName !== null && setEntryValue) { setEntryValue(category, channels[openMenu].index, 'name', newName); } setOpenMenu(null); }}>RENAME</button>
                  <button onClick={() => {
                    try {
                      if (!exportChannel) { alert('Copy not available'); setOpenMenu(null); return; }
                      const payload = exportChannel(category, channels[openMenu].index) || {};
                      localStorage.setItem(`uisnapshot_copy_${category}`, JSON.stringify(payload));
                      alert('Channel settings copied');
                    } catch (err) { alert('Copy failed'); }
                    setOpenMenu(null);
                  }}>COPY</button>
                  <button onClick={() => {
                    try {
                      if (!importChannel) { alert('Paste not available'); setOpenMenu(null); return; }
                      const raw = localStorage.getItem(`uisnapshot_copy_${category}`);
                      if (!raw) { alert('No copied settings for this channel type'); setOpenMenu(null); return; }
                      const data = JSON.parse(raw);
                      importChannel(category, channels[openMenu].index, data);
                      alert('Pasted settings');
                    } catch (err) { alert('Paste failed'); }
                    setOpenMenu(null);
                  }}>PASTE</button>
                </div>
              </div>
            </div>,
            document.body
          )
        )}
      </Card.Body>
    </Card>
  );
}
